import React, { useState } from "react";
import { Lock, ShieldCheck, ShieldOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../api";
import PasswordInput from "./PasswordInput";

const inputCls =
  "w-full bg-[#12131f] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-[#25D366] transition-colors";

const SecurityDialog = ({ open, onClose, user, onUpdated }) => {
  const [tab, setTab] = useState("password");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1b1d2b] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Security</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-4">
          <button onClick={() => setTab("password")} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${tab === "password" ? "bg-[#25D366] text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            <KeyRound className="w-3.5 h-3.5" /> Change Password
          </button>
          <button onClick={() => setTab("2fa")} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${tab === "2fa" ? "bg-[#25D366] text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            <ShieldCheck className="w-3.5 h-3.5" /> Two-Factor Auth
          </button>
        </div>
        {tab === "password" ? <ChangePassword /> : <TwoFA user={user} onUpdated={onUpdated} />}
      </DialogContent>
    </Dialog>
  );
};

const ChangePassword = () => {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (nw.length < 6) return setErr("New password must be at least 6 characters");
    if (nw !== confirm) return setErr("Passwords do not match");
    setLoading(true);
    try {
      await api.changePassword(cur, nw);
      setLoading(false);
      setCur(""); setNw(""); setConfirm("");
      toast.success("Password changed");
    } catch (e2) {
      setLoading(false);
      setErr(e2?.response?.data?.detail || "Failed to change password");
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="text-xs font-medium text-white/60 mb-1.5 block">Current Password</label>
        <PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Current password" />
      </div>
      <div>
        <label className="text-xs font-medium text-white/60 mb-1.5 block">New Password</label>
        <PasswordInput value={nw} onChange={(e) => setNw(e.target.value)} placeholder="At least 6 characters" />
      </div>
      <div>
        <label className="text-xs font-medium text-white/60 mb-1.5 block">Confirm New Password</label>
        <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password" />
      </div>
      {err && <p className="text-red-400 text-sm">{err}</p>}
      <button type="submit" disabled={loading} className="w-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg transition-colors">
        {loading ? "Saving..." : "Update Password"}
      </button>
    </form>
  );
};

const TwoFA = ({ user, onUpdated }) => {
  const enabled = !!user?.twofa_enabled;
  const [setup, setSetup] = useState(null); // { qr, secret }
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [disableVal, setDisableVal] = useState("");

  const startSetup = async () => {
    setErr("");
    try {
      const data = await api.twofaSetup();
      setSetup(data);
    } catch (e) {
      toast.error("Failed to start setup");
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.twofaEnable(code);
      setLoading(false);
      setSetup(null); setCode("");
      toast.success("2FA enabled");
      onUpdated && onUpdated();
    } catch (e2) {
      setLoading(false);
      setErr(e2?.response?.data?.detail || "Invalid code");
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      // Send the value both as code and password; backend accepts either.
      await api.twofaDisable(disableVal.replace(/\D/g, "").length === 6 ? disableVal : "", disableVal);
      setLoading(false);
      setDisableVal("");
      toast.success("2FA disabled");
      onUpdated && onUpdated();
    } catch (e2) {
      setLoading(false);
      setErr(e2?.response?.data?.detail || "Invalid code or password");
    }
  };

  if (enabled) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg px-3 py-2.5">
          <ShieldCheck className="w-5 h-5 text-[#25D366]" />
          <p className="text-sm text-white">Two-factor authentication is <span className="font-semibold text-[#25D366]">ON</span></p>
        </div>
        <form onSubmit={disable} className="space-y-2">
          <label className="text-xs font-medium text-white/60 block">Enter a current 6-digit code (or your password) to disable</label>
          <input value={disableVal} onChange={(e) => setDisableVal(e.target.value)} className={inputCls} placeholder="6-digit code or password" />
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-red-500/90 hover:bg-red-500 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg transition-colors">
            <ShieldOff className="w-4 h-4" /> {loading ? "Disabling..." : "Disable 2FA"}
          </button>
        </form>
      </div>
    );
  }

  if (!setup) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-white/60">Add an extra layer of security using an authenticator app (Google Authenticator, Authy, etc.).</p>
        <button onClick={startSetup} className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold py-2.5 rounded-lg transition-colors">
          <ShieldCheck className="w-4 h-4" /> Enable 2FA
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={enable} className="space-y-3">
      <p className="text-sm text-white/60">1. Scan this QR code with your authenticator app:</p>
      <div className="flex justify-center">
        <img src={setup.qr} alt="2FA QR" className="w-44 h-44 rounded-lg bg-white p-2" />
      </div>
      <p className="text-xs text-white/40 text-center break-all">Or enter key manually: <span className="text-white/70">{setup.secret}</span></p>
      <p className="text-sm text-white/60">2. Enter the 6-digit code to confirm:</p>
      <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" className={`${inputCls} tracking-[0.3em] text-center`} placeholder="000000" />
      {err && <p className="text-red-400 text-sm">{err}</p>}
      <button type="submit" disabled={loading || code.length !== 6} className="w-full bg-[#25D366] hover:bg-[#1fb757] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg transition-colors">
        {loading ? "Verifying..." : "Confirm & Enable"}
      </button>
    </form>
  );
};

export default SecurityDialog;
